import React, { useState, useEffect } from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Button,
  Input,
  Spinner,
  Divider,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { Tendering, Tender, TenderManagerTool } from '../../models/tendering';
import { UserSystemData } from '../../models/users';
import { SystemOption } from '../../models/systemOptions';
import { useAppContext } from '../../AppContext';
import { getSystemOptionsByID } from '../../services/systemOptionService';
import { postExternal } from '../../services/externalService';
import { Tag } from '../../models/tag';

interface TenderContactDrawerProps {
  isOpen: boolean;
  onDismiss: () => void;
  tender?: any;
  tenders?: any[];
  tendering?: Tendering;
  tags: Tag[];
}

const TenderContactDrawer: React.FC<TenderContactDrawerProps> = ({
  isOpen,
  onDismiss,
  tender,
  tenders,
  tendering,
  tags,
}) => {
  const [limeHeader, setLimeHeader] = useState('');
  const [limeText, setLimeText] = useState('');
  const [relatedTenders, setRelatedTenders] = useState<Tender[]>([]);
  const [systemOptions, setSystemOptions] = useState<SystemOption | null>(null);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');

  const { user } = useAppContext();

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    console.log(tenders);
    // Get system options
    try {
      const options = await getSystemOptionsByID(1);
      setSystemOptions(options);
    } catch (error) {
      console.error(error);
      setShowErrorModal(true);
    }

    setRelatedTenders(tenders ? tenders.filter((t: any )=> t.contactName[0] === tender.contactName[0] && t.tenderID !== tender.tenderID) : []);
  };

  const createLimeTask = async () => {
    if (!tender || !user || !systemOptions) return;

    setTicketLoading(true);
    const names = tender.contactName?.[0]?.toString().split(' ') || ['', ''];
    const lime = {
      event: 'new_ticket',
      counterpart: {
        email: tender?.contactEmail,
        firstname: names[0],
        lastname: names[1],
        phone: tender?.contactPhone,
      },
      title: limeHeader,
      content: limeText,
      agent: user.user.email,
    };

    try {
      const response = await postExternal(systemOptions.supportTicketLink, lime);
      console.log(response);
      setLimeHeader('');
      setLimeText('');
      setNotificationTitle('Ärende skickat!');
      setShowNotificationModal(true);
      setTicketLoading(false);
    } catch (error) {
      console.error(error);
      setTicketLoading(false);
      setShowErrorModal(true);
    }
  };

  const openTender = (id: number) => {
    window.open(`/#/tender/${id}`, '_blank')?.focus();
  };

  const handleClose = () => {
    setLimeHeader('');
    setLimeText('');
    onDismiss();
  };

  if (!tender) return null;

  return (
    <>
      <Drawer
        type="overlay"
        position="end"
        open={isOpen}
        onOpenChange={(_, { open }) => !open && handleClose()}
        size="large"
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                size='small'
                shape='circular'
                icon={<DismissRegular />}
                onClick={handleClose}
                aria-label="Close"
              />
            }
          >
            {tender.tenderName}
          </DrawerHeaderTitle>
        </DrawerHeader>

        <DrawerBody className="p-6">
          {/* Contact Information Section */}
          <div className="mt-4 mb-6">
            <h4 className="text-lg font-semibold mb-4">Kontaktuppgifter</h4>

            <h5 className="text-base font-medium mb-2">Verksamhet</h5>
            <div className="mb-4 space-y-2">
              <div className="flex gap-2">
                <span className="font-medium">Mobiltelefon:</span>
                <span>
                  <a
                    href={`tel:${tender.contactPhone}`}
                    className="text-blue-600 hover:underline"
                  >
                    {tender.contactPhone}
                  </a>
                </span>
              </div>
              <div className="flex gap-2">
                <span className="font-medium">E-post:</span>
                <span>
                  <a
                    href={`mailto:${tender.email}`}
                    className="text-blue-600 hover:underline"
                  >
                    {tender.email}
                  </a>
                </span>
              </div>
            </div>

            <h5 className="text-base font-medium mb-2">Föreståndare</h5>
            <div className="mb-4 space-y-2">
              <div className="flex gap-2">
                <span className="font-medium">Namn:</span>
                <span>{tender.contactName}</span>
              </div>
              <div className="flex gap-2">
                <span className="font-medium">Mobiltelefon:</span>
                <span>
                  <a
                    href={`tel:${tender.phone}`}
                    className="text-blue-600 hover:underline"
                  >
                    {tender.phone}
                  </a>
                </span>
              </div>
              <div className="flex gap-2">
                <span className="font-medium">E-post:</span>
                <span>
                  <a
                    href={`mailto:${tender.contactEmail}`}
                    className="text-blue-600 hover:underline"
                  >
                    {tender.contactEmail}
                  </a>
                </span>
              </div>
            </div>

            <h5 className="text-base font-medium mb-2">Placeringar</h5>
            <div className="mb-4 space-y-2">
              <div className="flex gap-2">
                <span className="font-medium">Mobiltelefon:</span>
                <span>
                  <a
                    href={`tel:${tender.contact?.phone || ''}`}
                    className="text-blue-600 hover:underline"
                  >
                    {tender.contact?.phone || ''}
                  </a>
                </span>
              </div>
              <div className="flex gap-2">
                <span className="font-medium">E-post:</span>
                <span>
                  <a
                    href={`mailto:${tender.contact?.email || ''}`}
                    className="text-blue-600 hover:underline"
                  >
                    {tender.contact?.email || ''}
                  </a>
                </span>
              </div>
            </div>
          </div>

          <Divider className="my-6" />

          {/* CRM Ticket Section */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold mb-4">Starta CRM ärende</h4>
            <div className="space-y-4">
              <div>
                <h5 className="text-base font-medium mb-2">Ärenderubrik</h5>
                <Input
                  className="w-full"
                  value={limeHeader}
                  onChange={(_, data) => setLimeHeader(data.value)}
                  disabled={ticketLoading}
                />
              </div>
              <div>
                <h5 className="text-base font-medium mb-2">
                  Meddelande (Skickas till leverantören)
                </h5>
                {!ticketLoading && (
                  <textarea
                    className="w-full h-64 p-3 border border-gray-300 rounded resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={limeText}
                    onChange={(e) => setLimeText(e.target.value)}
                    placeholder="Skriv ditt meddelande här..."
                  />
                )}
              </div>
              <Button
                appearance="primary"
                onClick={createLimeTask}
                disabled={ticketLoading || !limeHeader || !limeText}
                className="mt-4"
              >
                {ticketLoading ? (
                  <>
                    <Spinner size="tiny" className="mr-2" />
                    Ärendet skickas...
                  </>
                ) : (
                  'Skapa ärende'
                )}
              </Button>
            </div>
          </div>

          <Divider className="my-6" />

          {/* Related Tenders Section */}
          <div className="mb-4">
            <h4 className="text-lg font-semibold mb-4">Andra kopplingar</h4>
            <p className="mb-3">
              Vi hittade samma för- och efternamn med mail eller telefon hos följade
              leverantörer:
            </p>
            {relatedTenders.length > 0 ? (
              <ul className="list-disc pl-5 space-y-1">
                {relatedTenders.map((relatedTender) => (
                  <li key={relatedTender.tenderID}>
                    <a
                      href="#"
                      onClick={(e) => { e.preventDefault(); openTender(relatedTender.tenderID); }}
                      className="text-blue-600 hover:underline cursor-pointer"
                    >
                      {relatedTender.tenderName}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500">Inga kopplingar hittades</p>
            )}
          </div>
        </DrawerBody>
      </Drawer>

      {/* Error Modal */}
      {/* {showErrorModal && (
        <ErrorModal
          isOpen={showErrorModal}
          onDismiss={() => setShowErrorModal(false)}
        />
      )} */}

      {/* Notification Modal */}
      {/* {showNotificationModal && (
        <NotificationModal
          isOpen={showNotificationModal}
          onDismiss={() => setShowNotificationModal(false)}
          title={notificationTitle}
        />
      )} */}
    </>
  );
};

export default TenderContactDrawer
